import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, PageLoader, EmptyState } from '@/components/common/Layout';
import { KpiCard } from '@/components/common/KpiCard';
import { RiskBadge, RiskDot } from '@/components/common/RiskBadge';
import { CategoryBadge } from '@/components/common/CategoryBadge';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { fmtDate, fmtRel, SEVERITY_META } from '@/lib/format';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import { Waves, MountainSnow, HardHat, Flame, ArrowRight } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';
import { fmtFreshness } from '@/lib/format2';

const CAT_META = {
  water_change: { icon: Waves, label: 'Water change', color: '#3B82F6' },
  terrain_disturbance: { icon: MountainSnow, label: 'Terrain', color: '#F97316' },
  encroachment: { icon: HardHat, label: 'Encroachment', color: '#A855F7' },
  fire_flood: { icon: Flame, label: 'Fire / Flood', color: '#EF4444' },
};

export default function DashboardPage() {
  const [ovv, setOvv] = useState(null);
  const [recent, setRecent] = useState(null);
  usePageTitle('Infrastructure Command Centre');

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/overview').then(r => setOvv(r.data)),
      api.get('/dashboard/recent').then(r => setRecent(r.data)),
    ]).catch(() => {});
  }, []);

  if (!ovv || !recent) return <div className="p-6"><PageLoader label="Loading command centre…" /></div>;

  const sevChartData = Object.entries(ovv.alerts_by_severity).map(([k, v]) => ({
    name: k, count: v, fill: SEVERITY_META[k]?.hex || '#64748B',
  }));

  const catChartData = Object.entries(ovv.alerts_by_category).map(([k, v]) => ({
    name: CAT_META[k]?.label || k, count: v, fill: CAT_META[k]?.color || '#64748B',
  }));

  const trendData = ovv.alerts_over_time.length
    ? ovv.alerts_over_time
    : Array.from({ length: 7 }, (_, i) => ({ date: `d-${i}`, count: 0 }));

  const latestObs = recent.observations?.[0];
  const freshness = fmtFreshness(latestObs?.acquired_at);

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="dashboard-root">
      <PageHeader
        title="Infrastructure command centre"
        subtitle={<span>Live intelligence across all monitored assets. Alerts, incidents and risk trends. <span className="text-muted-foreground">·</span> Latest observation: <b className="text-foreground">{freshness.label}</b></span>}
        testId="dashboard-header"
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard label="Monitored assets" value={ovv.total_assets} testId="kpi-assets" />
        <KpiCard label="Active alerts" value={ovv.active_alerts} tone="warn" testId="kpi-active-alerts" />
        <KpiCard label="Critical alerts" value={ovv.critical_alerts} tone="danger" testId="kpi-critical-alerts" />
        <KpiCard label="Open incidents" value={ovv.open_incidents} tone="info" testId="kpi-open-incidents" />
        <KpiCard label="Observations" value={ovv.total_observations} testId="kpi-observations" />
        <KpiCard label="Portfolio risk" value={`${ovv.portfolio_risk_avg}`} hint="avg detection risk" tone={ovv.portfolio_risk_avg > 60 ? 'danger' : ovv.portfolio_risk_avg > 35 ? 'warn' : 'ok'} testId="kpi-portfolio-risk" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <Card className="p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Alerts over time (14d)</div>
            <Badge variant="outline" className="text-[10px]">org-scoped</Badge>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trendData}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 12 }} />
              <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4">
          <div className="text-sm font-semibold mb-3">Alerts by severity</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={sevChartData}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 12 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {sevChartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <Card className="p-4">
          <div className="text-sm font-semibold mb-3">Alerts by category</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={catChartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {catChartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Pie>
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Recent alerts</div>
            <Link to="/app/alerts"><Button size="sm" variant="ghost">View all <ArrowRight size={14} className="ml-1" /></Button></Link>
          </div>
          {recent.alerts.length === 0 ? <EmptyState title="No alerts yet" /> : (
            <div className="divide-y divide-border">
              {recent.alerts.slice(0, 6).map(a => (
                <Link to={`/app/alerts/${a.id}`} key={a.id} data-testid={`recent-alert-${a.id}`}
                      className={`flex items-center gap-3 py-2.5 hover:bg-secondary/60 rounded px-2 -mx-2 risk-stripe risk-stripe-${a.severity?.toLowerCase()}`}>
                  <RiskDot severity={a.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.title}</div>
                    <div className="text-xs text-muted-foreground">{fmtRel(a.created_at)} · {a.status}</div>
                  </div>
                  <CategoryBadge category={a.category} />
                  <RiskBadge severity={a.severity} />
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Open incidents</div>
            <Link to="/app/incidents"><Button size="sm" variant="ghost">View all <ArrowRight size={14} className="ml-1" /></Button></Link>
          </div>
          {recent.incidents.length === 0 ? <EmptyState title="No open incidents" /> : (
            <div className="divide-y divide-border">
              {recent.incidents.slice(0, 6).map(i => (
                <Link to={`/app/incidents/${i.id}`} key={i.id} data-testid={`recent-incident-${i.id}`}
                      className={`flex items-center gap-3 py-2.5 hover:bg-secondary/60 rounded px-2 -mx-2 risk-stripe risk-stripe-${i.severity?.toLowerCase()}`}>
                  <span className="font-mono text-xs text-muted-foreground w-24 shrink-0">{i.number}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{i.title}</div>
                    <div className="text-xs text-muted-foreground">{i.status} · {fmtRel(i.created_at)}</div>
                  </div>
                  <RiskBadge severity={i.severity} />
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Latest observations</div>
            <Link to="/app/observations"><Button size="sm" variant="ghost">View all <ArrowRight size={14} className="ml-1" /></Button></Link>
          </div>
          {recent.observations.length === 0 ? <EmptyState title="No observations yet" /> : (
            <div className="divide-y divide-border">
              {recent.observations.slice(0, 6).map(o => (
                <Link to={`/app/observations/${o.id}`} key={o.id}
                      className="flex items-center gap-3 py-2.5 hover:bg-secondary/60 rounded px-2 -mx-2">
                  <span className="h-2 w-2 rounded-full bg-primary" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{o.data_source} · {o.sensor}</div>
                    <div className="text-xs text-muted-foreground font-mono">{fmtDate(o.acquired_at)} · cloud {o.cloud_coverage_pct}%</div>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{o.quality}</Badge>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
